'use client';

import { useCallback, PropsWithChildren } from 'react';
import classNames from 'classnames';

import { PropsWithClassname } from '@/app/types';

interface ButtonRadioProps<ValueType = string> extends PropsWithChildren<PropsWithClassname> {
  value: ValueType;
  isChecked: boolean;
  onChange: (nextValue: ValueType) => void;
  label: string;
  disabled?: boolean;
  noteWhenSelected?: string;
}

function ButtonRadio<ValueType = string>({
  value,
  isChecked,
  onChange,
  label,
  disabled,
  noteWhenSelected,
  className,
  children,
}: ButtonRadioProps<ValueType>) {
  const handleChange = useCallback(() => {
    if (disabled) {
      return;
    }

    onChange(value);
  }, [onChange, disabled, value]);

  return (
    <div className={className}>
      <div
        onClick={handleChange}
        className={classNames(
          'group bg-white border-2 border-neutral-light hover:border-neutral-dark rounded-lg py-[16px] px-[8px] mb-[8px]',
          {
            'cursor-pointer': !disabled,
            'cursor-not-allowed': disabled,
            'border-primary-mid': isChecked,
          },
        )}
      >
        <div className="flex items-center">
          <input
            type="radio"
            value={value as string | number}
            checked={isChecked}
            className="ml-[2px] mr-[6px] w-[20px] h-[16px] accent-primary-mid group-hover:accent-primary-dark"
            disabled={disabled}
            onChange={handleChange}
          />
          <span>{label}</span>
        </div>
        {!!children && <div className="ml-[22px]">{children}</div>}
      </div>
      {noteWhenSelected && isChecked && (
        <small className="block text-[12px] leading-[1.4] text-neutral-mid mb-[32px]">{noteWhenSelected}</small>
      )}
    </div>
  );
}

export default ButtonRadio;
